import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { getBrazeSDK, initBrazeCore } from '../../Config/Braze/braze.config'
import { BrazePageTrackingComponent } from '../../Hooks/Braze/useBrazePageTracking'
import {
  useBrazeContentCards,
  BrazeContentCard,
} from '../../Hooks/Braze/useBrazeContentCards'
import { useAuthContext } from '../AuthContext/AuthContext'
import { isDevEnvironment } from '../../Utils'

type BrazeContextType = {
  brazeInitialized: boolean
  brazeError: Error | null
  contentCards: BrazeContentCard[]
}

export const BrazeContext = createContext<BrazeContextType | null>(null)

export const useBraze = () => {
  const context = useContext(BrazeContext)
  if (!context) {
    throw new Error('useBraze must be used within a BrazeProvider')
  }
  return context
}

type BrazeProviderProps = {
  children: React.ReactNode
}

export const BrazeProvider: React.FC<BrazeProviderProps> = ({ children }) => {
  const [brazeInitialized, setBrazeInitialized] = useState<boolean>(false)
  const [brazeError, setBrazeError] = useState<Error | null>(null)
  const { userId, authLoading, isLoggedIn, isAnonymous } = useAuthContext()

  const { contentCards } = useBrazeContentCards(brazeInitialized)

  const startBraze = useCallback(async () => {
    if (isDevEnvironment()) {
      console.log('[Braze] Starting Braze')
    }
    if (authLoading) return
    try {
      const braze = await getBrazeSDK()
      if (!braze) {
        throw new Error('Failed to load Braze SDK')
      }

      // Pass user ID if authenticated, undefined if anonymous
      const uid = userId ?? undefined

      if (isDevEnvironment()) {
        console.log('[Braze] Initializing with userId:', uid || 'anonymous')
      }

      const success = await initBrazeCore(uid)
      if (!success) {
        throw new Error('Failed to initialize Braze core')
      }

      setBrazeInitialized(true)
      setBrazeError(null)

      if (isDevEnvironment()) {
        console.log(
          '[Braze] Successfully initialized with user:',
          uid ? 'authenticated' : 'anonymous'
        )
      }
    } catch (error) {
      console.error('[Braze] Initialization failed:', error)
      setBrazeError(
        error instanceof Error ? error : new Error('Unknown error initializing Braze')
      )
      setBrazeInitialized(false)
    }
  }, [userId, authLoading])

  useEffect(() => {
    // Wait for auth before initializing Braze
    if (!authLoading) {
      if (isDevEnvironment()) {
        console.log('[Braze] Auth completed, initializing Braze. User state:', {
          isLoggedIn,
          isAnonymous,
          hasUserId: !!userId,
        })
      }
      startBraze()
    }
  }, [authLoading, isLoggedIn, isAnonymous, userId, startBraze])

  const contextValue = {
    brazeInitialized,
    brazeError,
    contentCards,
  }

  return (
    <BrazeContext.Provider value={contextValue}>
      {children}
      <BrazePageTrackingComponent brazeInitialized={brazeInitialized} />
    </BrazeContext.Provider>
  )
}
